/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  Row, Col, Card, CardBody, CardHeader, CardText,
  CardFooter, Button, Input, InputGroup,
} from 'reactstrap';
import moment from 'moment';
import { get, put } from '../../helpers/api_helper';

function PartnerApiReturn(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t, reference, onUpdated } = props;

  const [data, setData] = useState(null);
  const [concluding, setConcluding] = useState(false);
  const [signal, setSignal] = useState(0);
  const [totals, setTotals] = useState({});
  const [chargeShipping, setChargeShipping] = useState({});

  const handleTotalChange = (id, value) => {
    setTotals((prev) => ({ ...prev, [id]: value }));
  };

  const handleChargeShippingChange = (id, checked) => {
    setChargeShipping((prev) => ({ ...prev, [id]: checked }));
  };

  const [denialReasons, setDenialReasons] = useState({});
  const [denialComments, setDenialComments] = useState({});

  useEffect(() => {
    onUpdated();
  }, [signal]);

  const getReturns = async () => {
    try {
      const res = await get(`/api/partner/returns/${reference.reference1}`);

      if (res !== '') {
        setData(res);
      } else {
        setData(null);
      }
    } catch (e) {
      toastr.error(t('Error loading Partner API returns data'));
    }
  };

  useEffect(() => {
    getReturns();
  }, [reference, signal]);

  const issueCredit = async (id, amount) => {
    try {
      if ((denialComments[id] ?? '') !== '') {
        toastr.warning(t('Did you mean to deny instead? (please erase comment if not)'));

        return;
      }

      setConcluding(true);
      await put(`/api/partner/returns/credit/${id}`, {
        amount,
        charge_return_shipping: Boolean(chargeShipping[id]),
      });

      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message, t('Error crediting RGM'));
    } finally {
      setConcluding(false);
    }
  };

  const deny = async (id) => {
    const code = denialReasons[id] ?? '';
    const comment = denialComments[id] ?? '';

    if (code === '') {
      toastr.info(t('Please select a reason...', t('No reason selected')));

      return;
    }

    try {
      setConcluding(true);
      await put(`/api/partner/returns/deny/${id}`, {
        code,
        comment,
        charge_return_shipping: Boolean(chargeShipping[id]),
      });

      setSignal(signal + 1);
    } catch (error) {
      toastr.error(error.message, t('Error denying RGM'));
    } finally {
      setConcluding(false);
    }
  };

  const rmaTypeMap = (code) => {
    switch (code) {
      case 'damaged_in_transit':
        return 'Damaged in Transit';
      case 'defective_or_failed':
        return 'Defective / Failed Under Warranty';
      case 'wrong_item_shipped':
        return 'Wrong Item Shipped';
      case 'ordered_in_error':
        return 'Ordered in Error';
      case 'missing_parts':
        return 'Missing Parts / Accessories';
      case 'other':
        return 'Other (Explain Below)';
      default:
        return code;
    }
  };

  const jsx = (
    <>
      {data !== null && (
        <Row>
          {data.map((rma) => {
            const totalDefault = Number(
              rma.items.reduce((a, c) => a + c.qty * c.price, 0).toFixed(2),
            );
            const returnLabel = rma.return_label || null;
            const labelCost = Number(returnLabel?.cost) || 0;
            const canChargeShipping = labelCost > 0
              && !!returnLabel?.tracking_number
              && !returnLabel?.account_code;
            const total = totals[rma.id] ?? totalDefault;

            return (
              <Col lg="6" xl="5" xxl="5" key={`itm_${rma.id}`}>
                <Card>
                  <CardHeader className="bg-transparent border-bottom">
                    {t('Partner API Return')}
                  </CardHeader>
                  <CardBody>
                    <h6>
                      Return No:
                      {' '}
                      <strong>{rma.id}</strong>
                      <br />
                      <br />
                      <div className="hstack gap-3">
                        <strong>{t('Skus Returned')}</strong>
                        <div className="vstack gap-1">
                          {rma.items.map((x) => (
                            <div>
                              {x.listing.sku}
                              {' '}
                              <sup>
                                <em>{`x${x.qty}`}</em>
                              </sup>
                            </div>
                          ))}
                        </div>
                      </div>
                      <br />

                      <CardText>
                        <strong>{t('Return Reason Class:')}</strong>
                        {' '}
                        {rmaTypeMap(rma.reason_class)}
                      </CardText>

                      <strong>{t('Description')}</strong>
                      {': '}
                      <br />
                      {rma.reason_description}
                    </h6>
                    <CardText className="mt-3">
                      <strong>{t('Inbound Tracking:')}</strong>
                      {' '}
                      {rma.inbound_tracking?.toUpperCase()}
                    </CardText>
                    {returnLabel?.generated_at && (
                      <CardText>
                        <strong>{t('Return Label:')}</strong>
                        {' '}
                        {labelCost > 0 ? `$${labelCost.toFixed(2)}` : t('Generated')}
                        {returnLabel?.tracking_number && (
                          <>
                            {' '}
                            {`(${returnLabel.tracking_number})`}
                          </>
                        )}
                        <br />
                        <small className="theme-body-muted">
                          {moment(returnLabel.generated_at).format('lll')}
                        </small>
                        {!returnLabel?.account_code && (
                          <>
                            <br />
                            <small className="theme-body-muted">
                              {t('Label billed to ShopJimmy shipping account')}
                            </small>
                          </>
                        )}
                      </CardText>
                    )}
                    <CardText>
                      <strong>{t('Value')}</strong>
                      {' '}
                      {rma.items.reduce((a, c) => a + (c.price * c.qty), 0).toFixed(2)}
                      {' '}
                      USD
                    </CardText>
                  </CardBody>
                  <CardFooter>
                    <Row className="g-4 align-items-start">
                      {rma.credited_at === null && rma.denied_at === null && (
                        <>
                          {canChargeShipping && (
                            <Col xs="12">
                              <div
                                className="rounded border p-3 p-md-4"
                                style={{
                                  borderColor: 'rgba(255,255,255,0.14)',
                                  backgroundColor: 'rgba(255,255,255,0.03)',
                                }}
                              >
                                <div className="d-flex flex-column gap-2">
                                  <div className="small text-uppercase fw-semibold theme-body-subtle" style={{ letterSpacing: '0.06em' }}>
                                    {t('Return Shipping Decision')}
                                  </div>
                                  <div className="form-check mb-0">
                                    <input
                                      id={`charge_return_shipping_${rma.id}`}
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={Boolean(chargeShipping[rma.id])}
                                      onChange={(e) => handleChargeShippingChange(
                                        rma.id,
                                        e.target.checked,
                                      )}
                                    />
                                    <label
                                      className="form-check-label fw-semibold"
                                      htmlFor={`charge_return_shipping_${rma.id}`}
                                    >
                                      <span className="theme-body-emphasis">
                                        {t('Charge customer for return shipping')}
                                        {' '}
                                        {`($${labelCost.toFixed(2)})`}
                                      </span>
                                    </label>
                                  </div>
                                  <div className="small theme-body-muted" style={{ lineHeight: 1.55 }}>
                                    {t('This applies to either outcome. On approval, shipping reduces the refund first and invoices any remaining balance. On denial, shipping is invoiced in full.')}
                                  </div>
                                </div>
                              </div>
                            </Col>
                          )}
                          <Col lg="5">
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <label
                                  htmlFor={`credit_amount_${rma.id}`}
                                  className="form-label mb-2 theme-body-muted"
                                >
                                  {t('Credit Amount')}
                                </label>
                                <input
                                  id={`credit_amount_${rma.id}`}
                                  type="number"
                                  className="form-control"
                                  step=".01"
                                  min="0"
                                  max={totalDefault}
                                  value={total}
                                  onChange={(e) => handleTotalChange(
                                    rma.id,
                                    parseFloat(e.target.value) || 0,
                                  )}
                                />
                              </div>
                              <Button
                                color="danger"
                                disabled={concluding}
                                className="btn btn-danger btn-label"
                                size="sm"
                                onClick={() => { issueCredit(rma.id, total); }}
                              >
                                <i className={`bx bx-check-double label-icon ${concluding && 'bx-spin'}`} />
                                {concluding ? t('Processing...') : t('Issue Credit')}
                              </Button>
                            </div>
                          </Col>
                          <Col lg="1" className="d-flex justify-content-center align-items-center">
                            <div
                              className="fs-4 fw-semibold theme-body-emphasis"
                              style={{ paddingTop: '2.75rem' }}
                            >
                              OR
                            </div>
                          </Col>
                          <Col lg="6">
                            <div className="d-flex flex-column gap-3">
                              <div>
                                <label
                                  htmlFor={`deny_reason_${rma.id}`}
                                  className="form-label mb-2 theme-body-muted"
                                >
                                  {t('Denial Reason')}
                                </label>
                                <InputGroup size="sm">
                                  <Input
                                    id={`deny_reason_${rma.id}`}
                                    type="select"
                                    value={denialReasons[rma.id] ?? ''}
                                    onChange={(e) => {
                                      const { value } = e.target;
                                      setDenialReasons((prev) => ({ ...prev, [rma.id]: value }));
                                    }}
                                  >
                                    <option value="">{t('-- Please select --')}</option>
                                    <option value="used_part_returned">{t('Used part returned')}</option>
                                    <option value="took_too_long">{t('Took too long to receive')}</option>
                                    <option value="wrong_part_received">{t('Wrong part received')}</option>
                                    <option value="no_part_received">{t('No part received')}</option>
                                    <option value="damaged_part_received">{t('Damaged Part received')}</option>
                                  </Input>
                                  <Button color="danger" disabled={concluding} className="btn-soft-danger" size="sm" onClick={() => { deny(rma.id); }}>
                                    {t('Deny Claim')}
                                  </Button>
                                </InputGroup>
                              </div>
                              <div>
                                <label
                                  htmlFor={`deny_comment_${rma.id}`}
                                  className="form-label mb-2 theme-body-muted"
                                >
                                  {t('Comments')}
                                </label>
                                <textarea
                                  id={`deny_comment_${rma.id}`}
                                  value={denialComments[rma.id] ?? ''}
                                  onChange={(e) => {
                                    const { value } = e.target;
                                    setDenialComments((prev) => ({ ...prev, [rma.id]: value }));
                                  }}
                                  rows="3"
                                  placeholder={t('Comments')}
                                  className="form-control"
                                />
                              </div>
                            </div>
                          </Col>
                        </>
                      )}

                      {rma.credited_at !== null && (
                        <Col>
                          <div className="hstack gap-2">
                            <Button color="success" disabled className="btn btn-success btn-label" size="sm">
                              <i className="bx bx-check-double label-icon" />
                              {rma.refund !== null && (
                                <strong>{`$${rma.refund.refund_amount}`}</strong>
                              )}
                              {' '}
                              {t('Credited')}
                            </Button>

                            <div className="text-muted">
                              {`by ${rma.credited_by} - ${moment(rma.credited_at).fromNow()}`}
                            </div>
                          </div>
                        </Col>
                      )}

                      {rma.denied_at !== null && (
                        <Col>
                          <div className="hstack gap-2">
                            <Button color="danger" disabled className="btn btn-success btn-label" size="sm">
                              <i className="mdi mdi-close-thick label-icon" />
                              {t('Denied')}
                            </Button>

                            <div className="text-muted">
                              {`by ${rma.denied_by} - ${moment(rma.denied_at).fromNow()}`}
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>

                    {rma.denied_at !== null && rma.comment !== null && rma.comment.trim() !== '' && (
                      <>
                        <div className="mt-3">
                          <strong>{t('Comment')}</strong>
                        </div>
                        <blockquote className="p-3 pt-1 ps-0 mb-0">{rma.comment}</blockquote>
                      </>
                    )}
                  </CardFooter>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {data === null && (
        <div />
      )}
    </>
  );

  return jsx;
}

PartnerApiReturn.defaultProps = {
  t: (w) => w,
};

PartnerApiReturn.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  reference: PropTypes.object.isRequired,
  onUpdated: PropTypes.func.isRequired,
};

export default connect(() => ({

}), {})(withTranslation()(PartnerApiReturn));
